/**
 * Minimal toast queue for the theme demo. Not the app-wide notification
 * system ([[admin-reusable-ui-primitives]] already covers that via
 * react-notifications-component) — this only backs the Toast spec's own
 * component for visual QA.
 *
 * Queue behavior per spec: newer toast replaces the current one rather than
 * stacking; dismiss timer pauses on hover/focus; default duration is 2200ms,
 * extended to 6000ms when the toast carries an action.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Toast from "./Toast";

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  const startTimer = useCallback(
    (duration) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(dismiss, duration);
    },
    [dismiss]
  );

  const showToast = useCallback(
    ({ variant = "success", message, action, duration }) => {
      const resolvedDuration = duration ?? (action ? 6000 : 2200);
      setToast({ variant, message, action, duration: resolvedDuration });
      startTimer(resolvedDuration);
    },
    [startTimer]
  );

  const pause = () => clearTimeout(timerRef.current);
  const resume = () => toast && startTimer(toast.duration);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {createPortal(
        <Toast toast={toast} onDismiss={dismiss} onPause={pause} onResume={resume} />,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
};
